import { BaseService } from "./base.service";
import {
  Form,
  Question,

  Validation,

  Condition,
  QuestionType,
} from "../types/forms";
import { api } from "../client/axiosClient";

// UpdateFormDto - same as CreateFormRequest for updates
export interface UpdateFormDto {
  title?: string;
  description?: string;
  connector?: string;
  stringId?: string;
  questions?: CreateQuestionRequest[];
}

export interface CreateFormRequest {
  title: string;
  description?: string;
  connector?: string;
  stringId?: string;
  questions: CreateQuestionRequest[];
}

export interface CreateQuestionRequest {
  id?: string; // UUID - for reusing existing questions on update (preserves answer references)
  stringId: string;
  type: string;
  text: string;
  helpText?: string;
  mandatory: boolean;
  placeholder?: string;
  defaultValue?: string;
  validation?: string;
  displayLogicConditionType: string;
  displayLogicConditions?: string;
  displayLogicSourceMultiselectId?: string;
  displayLogicSourceMultiselectStringId?: string;
  displayLogicContextLabelTemplate?: string;
  options?: CreateQuestionOptionRequest[];
}

export interface CreateQuestionOptionRequest {
  value: string;
  label: string;
  order: number;
  color?: string | null;
}

export interface FormDto {
  id: string;
  organizationId: string;
  stringId?: string;
  title: string;
  description?: string;
  connector?: string;
  questions: QuestionDto[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface QuestionDto {
  id: string;
  stringId: string;
  type: string;
  text: string;
  helpText?: string;
  mandatory: boolean;
  placeholder?: string;
  defaultValue?: string;
  validation?: string;
  displayLogicConditionType: string;
  displayLogicConditions?: string;
  displayLogicSourceMultiselectId?: string;
  displayLogicContextLabelTemplate?: string;
  options?: QuestionOptionDto[];
  isActive: boolean;
}

export interface QuestionOptionDto {
  id: string;
  questionId: string;
  value: string;
  label: string;
  order: number;
  color?: string | null;
}

export interface FormSummaryDto {
  id: string;
  stringId?: string;
  title: string;
  description?: string;
  isActive: boolean;
  questionCount: number;
  updatedAt: string;
}

/**
 * FormService handles CRUD operations for forms.
 *
 * URL Pattern: /api/v1/forms
 * Organization context is provided via X-Organization-Id header (injected by axiosClient).
 *
 * Migration: The orgId parameter has been removed from URL paths. The backend
 * extracts organization context from the X-Organization-Id header for tenant scoping.
 */
class FormService {
  private basePath = "/api/v1/forms";
  /**
   * Get all forms for the current organization (from X-Organization-Id header).
   * @param _orgId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param activeOnly - Filter to active forms only
   */
  getAll(_orgId: string, activeOnly = true) {
    return api.get<FormDto[]>(this.basePath, {
      params: { activeOnly },
    });
  }

  /**
   * Get a form by ID.
   * @param _orgId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param formId - Form UUID
   */
  getById(_orgId: string, formId: string) {
    return api.get<FormDto>(`${this.basePath}/${formId}`);
  }

  /**
   * Create a new form.
   * @param _orgId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param data - Form creation data
   */
  create(_orgId: string, data: CreateFormRequest): Promise<any> {
    return api.post<FormDto>(this.basePath, data);
  }

  /**
   * Update an existing form.
   * @param _orgId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param formId - Form UUID
   * @param data - Form update data
   */
  update(_orgId: string, formId: string, data: UpdateFormDto): Promise<any> {
    return api.put<FormDto>(`${this.basePath}/${formId}`, data);
  }

  /**
   * Delete a form (soft delete).
   * @param _orgId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param formId - Form UUID
   */
  delete(_orgId: string, formId: string): Promise<any> {
    return api.delete(`${this.basePath}/${formId}`);
  }

  /**
   * Get forms by string ID (legacy identifier).
   * @param _orgId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param stringId - Legacy string identifier
   */
  getByStringId(_orgId: string, stringId: string) {
    return api.get<FormDto[]>(`${this.basePath}/by-string-id/${stringId}`);
  }

  /**
   * Get forms not linked to any program version item ("orphan" forms),
   * available for linking.
   * @param _orgId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   */
  getOrphans(_orgId: string) {
    return api.get<FormSummaryDto[]>(`${this.basePath}/orphans`);
  }

  /**
   * Batch retrieval: get multiple forms by their IDs.
   * Organization context comes from X-Organization-Id header.
   *
   * @param _orgId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param formIds - Array of form UUIDs
   * @returns Map of form ID to FormDto
   */
  getByIds(_orgId: string, formIds: string[]) {
    return api.post<Record<string, FormDto>>(`${this.basePath}/batch`, { ids: formIds });
  }

  /**
   * Update the color of a question option (for MAP graph visualization).
   */
  updateOptionColor(questionId: string, optionId: string, color: string) {
    return api.put(`${this.basePath}/questions/${questionId}/options/${optionId}/color`, { color });
  }
}

export const formService = new FormService();

// ===== Mapper functions for FormDto ↔ Form conversion =====

/**
 * Convert backend FormDto to frontend Form type for FormCreator
 */
export function formDtoToForm(dto: FormDto): Form {
  const questions = dto.questions.map(questionDtoToQuestion);
  resolveForEachSourceReferences(questions, dto.questions);
  return {
    uuid: dto.id, // Backend UUID for API operations
    form_id: dto.stringId || dto.id,
    organizationId: dto.organizationId,
    stringId: dto.stringId,
    connector: dto.connector || null,
    title: dto.title,
    description: dto.description || null,
    questions,
  };
}

/**
 * The backend stores for_each source references as question UUIDs, but the
 * FormCreator works with stringIds. Resolve UUID references to stringIds so
 * the editor selection and validation match; unresolvable references are
 * kept as-is so validation flags them explicitly.
 */
function resolveForEachSourceReferences(questions: Question[], dtos: QuestionDto[]): void {
  const stringIdByUuid = new Map<string, string>();
  dtos.forEach((dto) => {
    if (dto.id && dto.stringId) {
      stringIdByUuid.set(dto.id, dto.stringId);
    }
  });
  questions.forEach((question) => {
    const sourceId = question.display_logic.source_multiselect_id;
    if (sourceId) {
      question.display_logic.source_multiselect_id = stringIdByUuid.get(sourceId) || sourceId;
    }
  });
}

/**
 * Convert backend QuestionDto to frontend Question type
 */
function questionDtoToQuestion(dto: QuestionDto): Question {
  return {
    id: dto.stringId, // Use stringId as the legacy ID
    uuid: dto.id, // Backend UUID as the new UUID
    text: dto.text,
    help_text: dto.helpText || null,
    type: dto.type.toLowerCase() as QuestionType,
    mandatory: dto.mandatory,
    placeholder: dto.placeholder || null,
    default_value: dto.defaultValue || null,
    validation: dto.validation ? parseValidation(dto.validation) : null,
    display_logic: {
      condition_type: dto.displayLogicConditionType.toLowerCase() as "always" | "if" | "for_each",
      conditions: dto.displayLogicConditions
        ? parseDisplayLogicConditions(dto.displayLogicConditions)
        : null,
      source_multiselect_id: dto.displayLogicSourceMultiselectId || null,
      context_label_template: dto.displayLogicContextLabelTemplate || null,
    },
    options:
      dto.options?.map((opt) => ({
        value: opt.value,
        label: opt.label,
        color: opt.color || null,
      })) || null,
    attachments: [], // Empty array for FormCreator
  };
}

/**
 * Parse validation JSON string to Validation object
 */
function parseValidation(validationJson: string): Validation | null {
  try {
    return JSON.parse(validationJson) as Validation;
  } catch {
    return null;
  }
}

/**
 * Parse display logic conditions JSON string
 */
function parseDisplayLogicConditions(conditionsJson: string): Condition[][] | null {
  try {
    return JSON.parse(conditionsJson) as Condition[][];
  } catch {
    return null;
  }
}

/**
 * Convert frontend Form to CreateFormRequest for backend
 */
export function formToCreateFormRequest(form: Form, _organizationId: string): CreateFormRequest {
  return {
    title: form.title,
    description: form.description || undefined,
    connector: form.connector || undefined,
    stringId: form.form_id,
    questions: form.questions.map((q) => questionToCreateQuestionRequest(q, form.questions)),
  };
}

/**
 * Convert frontend Form to UpdateFormDto for backend
 */
export function formToUpdateFormDto(form: Form): UpdateFormDto {
  return {
    title: form.title,
    description: form.description || undefined,
    connector: form.connector || undefined,
    stringId: form.form_id,
    questions: form.questions.map((q) => questionToCreateQuestionRequest(q, form.questions)),
  };
}

/**
 * Convert frontend Question to CreateQuestionRequest for backend
 * @param question The question to convert
 * @param allQuestions All questions in the form (needed to resolve UUID to stringId)
 */
function questionToCreateQuestionRequest(
  question: Question,
  allQuestions?: Question[]
): CreateQuestionRequest {
  // Resolve source_multiselect_id: it could be a UUID or a stringId
  // Backend expects stringId, so we need to convert UUID -> stringId
  let sourceMultiselectStringId = question.display_logic.source_multiselect_id || undefined;

  if (sourceMultiselectStringId && allQuestions) {
    // Check if it's a UUID (contains hyphens in standard format)
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      sourceMultiselectStringId
    );
    if (isUuid) {
      // Find the question with this UUID and get its stringId
      const sourceQuestion = allQuestions.find((q) => q.uuid === sourceMultiselectStringId);
      if (sourceQuestion) {
        sourceMultiselectStringId = sourceQuestion.id; // id is stringId
      }
    }
  }

  return {
    id: question.uuid, // Include UUID to reuse existing questions on update
    stringId: question.id,
    type: question.type.toUpperCase(),
    text: question.text,
    helpText: question.help_text || undefined,
    mandatory: question.mandatory,
    placeholder: question.placeholder || undefined,
    defaultValue: question.default_value?.toString() || undefined,
    validation: question.validation ? JSON.stringify(question.validation) : undefined,
    displayLogicConditionType: question.display_logic.condition_type.toUpperCase(),
    displayLogicConditions: question.display_logic.conditions
      ? JSON.stringify(question.display_logic.conditions)
      : undefined,
    // Use stringId for FOR_EACH source references during create/update
    displayLogicSourceMultiselectStringId: sourceMultiselectStringId,
    displayLogicContextLabelTemplate: question.display_logic.context_label_template || undefined,
    options: question.options?.map((opt, idx) => ({
      value: opt.value,
      label: opt.label,
      order: idx,
      color: opt.color || null,
    })),
  };
}
